import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildMessagesReportFetchKey,
  formatMessagesReportBoundaryDate,
} from "../../lib/utils/messagesReport"
import { normalizePulseApiPayload } from "../../lib/utils/pulseApiRows"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

/**
 * GET /api/v1/report/messages/clients/{clientId}/messages?startDate=&endDate=
 */
export const fetchClientMessagesReport = createAsyncThunk(
  "clientMessagesReport/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const start = formatMessagesReportBoundaryDate(startDate)
      const end = formatMessagesReportBoundaryDate(endDate)
      const query = new URLSearchParams({ startDate: start, endDate: end }).toString()
      const data = await get(
        `report/messages/clients/${clientId}/messages?${query}`,
      )

      return {
        rows: normalizePulseApiPayload(data),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildMessagesReportFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch messages report")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { clientId, startDate, endDate } = arg || {}
      const start = formatMessagesReportBoundaryDate(startDate)
      const end = formatMessagesReportBoundaryDate(endDate)
      const key = buildMessagesReportFetchKey(clientId, start, end)
      return !skipIfFetchKeyLoaded(getState().clientMessagesReport, key)
    },
  },
)

const initialState = {
  rows: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginClientMessagesReportFetch(state, action) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatMessagesReportBoundaryDate(startDate)
  const end = formatMessagesReportBoundaryDate(endDate)
  state.status = "loading"
  state.error = null
  state.requestedFetchKey = buildMessagesReportFetchKey(clientId, start, end)
  state.fetchedFetchKey = null
  state.rows = []
}

function isStaleClientMessagesReportFetch(action, state) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatMessagesReportBoundaryDate(startDate)
  const end = formatMessagesReportBoundaryDate(endDate)
  const key = buildMessagesReportFetchKey(clientId, start, end)
  return key !== String(state.requestedFetchKey ?? "")
}

const clientMessagesReportSlice = createSlice({
  name: "clientMessagesReport",
  initialState,
  reducers: {
    clearClientMessagesReport: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientMessagesReport.pending, (state, action) => {
        beginClientMessagesReportFetch(state, action)
      })
      .addCase(fetchClientMessagesReport.fulfilled, (state, action) => {
        if (isStaleClientMessagesReportFetch(action, state)) return
        state.status = "succeeded"
        state.rows = action.payload?.rows ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientMessagesReport.rejected, (state, action) => {
        if (isStaleClientMessagesReportFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch messages report"
        state.rows = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientMessagesReport } = clientMessagesReportSlice.actions
export default clientMessagesReportSlice.reducer
